/**
 * SiteContentPanel — edit the public About / founder narrative and manage
 * testimonials live (no redeploy). Two cards: "About & founder" and
 * "Testimonials".
 */
import React, { useCallback, useEffect, useState } from "react";
import { Save, Plus, Trash2, Eye, EyeOff, RefreshCw, Upload, GripVertical, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const Field = ({ label, value, onChange, rows, testid, placeholder }) => (
  <label className="block">
    <span className="text-[12px] font-medium ek-text-muted">{label}</span>
    {rows ? (
      <textarea
        rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6]"
        data-testid={testid}
      />
    ) : (
      <input
        value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6]"
        data-testid={testid}
      />
    )}
  </label>
);

function AboutEditor() {
  const [about, setAbout] = useState({});
  const [founder, setFounder] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content/about");
      setAbout(data.about || {});
      setFounder(data.founder || {});
    } catch { toast.error("Failed to load content"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/founder-photo", fd);
      setFounder((f) => ({ ...f, photo_url: data.photo_url }));
      toast.success("Photo uploaded — live on the site now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        about: { ...about },
        founder: { ...founder, expertise: (founder.expertiseText ?? (founder.expertise || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean) },
      };
      delete payload.founder.expertiseText;
      const { data } = await api.put("/admin/content/about", payload);
      setAbout(data.about || {}); setFounder(data.founder || {});
      toast.success("Saved — live on the site now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-[13px] ek-text-muted">Loading…</div>;

  const expVal = founder.expertiseText ?? (Array.isArray(founder.expertise) ? founder.expertise.join("\n") : "");

  return (
    <div className="ek-surface border ek-line p-6 space-y-5" data-testid="about-editor">
      <div className="flex items-center justify-between">
        <h3 className="ek-heading text-lg">About &amp; founder</h3>
        <span className="text-[11px] ek-text-muted">Public — edits appear instantly</span>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Hero intro" rows={3} value={about.hero_intro} onChange={(v) => setAbout({ ...about, hero_intro: v })} testid="about-hero-intro-input" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Vision" rows={3} value={about.vision} onChange={(v) => setAbout({ ...about, vision: v })} testid="about-vision-input" />
          <Field label="Mission" rows={3} value={about.mission} onChange={(v) => setAbout({ ...about, mission: v })} testid="about-mission-input" />
        </div>
        <Field label="Story heading" value={about.story_heading} onChange={(v) => setAbout({ ...about, story_heading: v })} testid="about-story-heading-input" />
        <Field label="Story (blank line = new paragraph)" rows={6} value={about.story} onChange={(v) => setAbout({ ...about, story: v })} testid="about-story-input" />
      </div>

      <div className="border-t ek-line pt-5 grid grid-cols-1 gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Founder name" value={founder.name} onChange={(v) => setFounder({ ...founder, name: v })} testid="founder-name-input" />
          <Field label="Founder role" value={founder.role} onChange={(v) => setFounder({ ...founder, role: v })} testid="founder-role-input" />
        </div>
        <Field label="Founder photo URL" value={founder.photo_url} onChange={(v) => setFounder({ ...founder, photo_url: v })} testid="founder-photo-input" placeholder="https://…" />
        <div className="flex items-center gap-3 -mt-2">
          {founder.photo_url ? <img src={founder.photo_url} alt="Founder" className="w-12 h-14 object-cover border ek-line" data-testid="founder-photo-preview" /> : null}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadPhoto} className="hidden" data-testid="founder-photo-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="ek-btn ek-btn-secondary text-[12px]" data-testid="founder-photo-upload-btn">
            {uploading ? <RefreshCw size={13} className="animate-spin" /> : <Upload size={13} />} {uploading ? "Uploading…" : "Upload photo"}
          </button>
        </div>
        <Field label="Founder bio (blank line = new paragraph)" rows={6} value={founder.bio} onChange={(v) => setFounder({ ...founder, bio: v })} testid="founder-bio-input" />
        <Field label="Founder quote" rows={2} value={founder.quote} onChange={(v) => setFounder({ ...founder, quote: v })} testid="founder-quote-input" />
        <Field label="Network note" rows={2} value={founder.network_note} onChange={(v) => setFounder({ ...founder, network_note: v })} testid="founder-network-input" />
        <Field label="Areas of expertise (one per line)" rows={4} value={expVal} onChange={(v) => setFounder({ ...founder, expertiseText: v })} testid="founder-expertise-input" />
      </div>

      <button onClick={save} disabled={saving} className="ek-btn ek-btn-primary text-[13px]" data-testid="about-save-btn">
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save content
      </button>
    </div>
  );
}

const EMPTY = { quote: "", name: "", role: "", company: "", published: false, order: 0 };

function TestimonialsEditor() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/content/testimonials"); setItems(data.testimonials || []); }
    catch { toast.error("Failed to load testimonials"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async (ordered) => {
    try { await api.put("/admin/content/testimonials-order", { ids: ordered.map((t) => t.id) }); toast.success("Order saved"); }
    catch { toast.error("Reorder failed"); load(); }
  };
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const from = items.findIndex((t) => t.id === dragId);
    const to = items.findIndex((t) => t.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDragId(null);
    persistOrder(next);
  };

  const add = async () => {
    if (!draft.quote.trim() || !draft.name.trim()) { toast.error("Quote and name are required"); return; }
    try { await api.post("/admin/content/testimonials", { ...draft, order: Number(draft.order) || 0 }); setDraft(EMPTY); toast.success("Testimonial added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const togglePublish = async (t) => {
    try { await api.put(`/admin/content/testimonials/${t.id}`, { ...t, published: !t.published }); load(); toast.success(t.published ? "Hidden" : "Published"); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/content/testimonials/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="ek-surface border ek-line p-6 space-y-5" data-testid="testimonials-editor">
      <div className="flex items-center justify-between">
        <h3 className="ek-heading text-lg">Testimonials</h3>
        <span className="text-[11px] ek-text-muted">Published ones show on Home &amp; About</span>
      </div>

      <div className="border ek-line p-4 space-y-3 ek-surface-2">
        <Field label="Quote" rows={3} value={draft.quote} onChange={(v) => setDraft({ ...draft, quote: v })} testid="new-testimonial-quote" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="new-testimonial-name" />
          <Field label="Role" value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} testid="new-testimonial-role" />
          <Field label="Company" value={draft.company} onChange={(v) => setDraft({ ...draft, company: v })} testid="new-testimonial-company" />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-[12px] ek-text-muted">
            <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} data-testid="new-testimonial-published" /> Publish now
          </label>
          <label className="flex items-center gap-2 text-[12px] ek-text-muted">
            Order <input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: e.target.value })} className="w-16 border ek-line px-2 py-1" data-testid="new-testimonial-order" />
          </label>
          <button onClick={add} className="ek-btn ek-btn-primary text-[13px] ml-auto" data-testid="add-testimonial-btn"><Plus size={14} /> Add</button>
        </div>
      </div>

      {loading ? (
        <div className="text-[13px] ek-text-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[13px] ek-text-muted" data-testid="testimonials-empty">No testimonials yet. Add your first one above — it stays hidden until you publish it.</div>
      ) : (
        <ul className="space-y-2" data-testid="testimonials-list">
          {items.map((t) => (
            <li
              key={t.id}
              draggable
              onDragStart={() => setDragId(t.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(t.id)}
              className={`flex items-start gap-3 border p-3 ek-surface ${dragId === t.id ? "border-[#7737F6] opacity-60" : "ek-line"}`}
              data-testid={`admin-testimonial-${t.id}`}
            >
              <span className="cursor-grab ek-text-muted pt-0.5" title="Drag to reorder" data-testid={`drag-testimonial-${t.id}`}><GripVertical size={16} /></span>
              <div className="flex-1">
                <div className="text-[13px] ek-text-soft">“{t.quote}”</div>
                <div className="text-[12px] ek-text-muted mt-1">{[t.name, t.role, t.company].filter(Boolean).join(" · ")} · order {t.order}</div>
              </div>
              <button onClick={() => togglePublish(t)} title={t.published ? "Published — click to hide" : "Hidden — click to publish"} className={`p-1.5 ${t.published ? "text-emerald-600" : "ek-text-muted"}`} data-testid={`toggle-testimonial-${t.id}`}>
                {t.published ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => remove(t.id)} className="p-1.5 text-red-500" data-testid={`delete-testimonial-${t.id}`}><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const LEGAL_PAGES = [
  { key: "privacy", label: "Privacy Policy" },
  { key: "cookies", label: "Cookie Policy" },
  { key: "terms", label: "Terms of Use" },
];

function LegalSignoffEditor() {
  const [legal, setLegal] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/content/about"); setLegal(data.legal || {}); }
    catch { toast.error("Failed to load legal sign-off"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (page, field, value) =>
    setLegal((l) => ({ ...l, [page]: { ...(l[page] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content/about", { legal });
      setLegal(data.legal || {});
      toast.success("Legal sign-off saved — live on the site now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-[13px] ek-text-muted">Loading…</div>;

  return (
    <div className="ek-surface border ek-line p-6 space-y-5" data-testid="legal-signoff-editor">
      <div className="flex items-center justify-between">
        <h3 className="ek-heading text-lg">Legal sign-off &amp; document control</h3>
        <span className="text-[11px] ek-text-muted">Shows on Privacy / Cookies / Terms</span>
      </div>
      <p className="text-[12px] ek-text-muted -mt-2">
        Set the version, effective &amp; review dates and reviewer for each legal page. These render as a
        document-control block at the foot of the page.
      </p>

      <div className="grid grid-cols-1 gap-4">
        {LEGAL_PAGES.map(({ key, label }) => {
          const v = legal[key] || {};
          return (
            <div key={key} className="border ek-line p-4 ek-surface-2" data-testid={`legal-editor-${key}`}>
              <div className="text-[13px] font-semibold ek-text mb-3">{label}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Version" value={v.version} onChange={(val) => setField(key, "version", val)} testid={`legal-${key}-version`} placeholder="1.0" />
                <Field label="Effective date" value={v.effective_date} onChange={(val) => setField(key, "effective_date", val)} testid={`legal-${key}-effective`} placeholder="August 2025" />
                <Field label="Last reviewed" value={v.reviewed_date} onChange={(val) => setField(key, "reviewed_date", val)} testid={`legal-${key}-reviewed`} placeholder="August 2025" />
                <Field label="Reviewed by" value={v.reviewed_by} onChange={(val) => setField(key, "reviewed_by", val)} testid={`legal-${key}-reviewedby`} placeholder="Ekselens Consulting" />
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} className="ek-btn ek-btn-primary text-[13px]" data-testid="legal-signoff-save-btn">
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save legal sign-off
      </button>
    </div>
  );
}

function SiteVersionEditor() {
  const [sv, setSv] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/content/about"); setSv(data.site_version || {}); }
    catch { toast.error("Failed to load site version"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content/about", { site_version: sv });
      setSv(data.site_version || {});
      toast.success("Site version saved — live on the About page now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-[13px] ek-text-muted">Loading…</div>;

  const buildTag = process.env.REACT_APP_RELEASE || "dev";
  const mismatch = sv.version && buildTag !== "dev" && !buildTag.includes(sv.version);

  return (
    <div className="ek-surface border ek-line p-6 space-y-5" data-testid="site-version-editor">
      <div className="flex items-center justify-between">
        <h3 className="ek-heading text-lg">Site version &amp; release</h3>
        <span className="text-[11px] ek-text-muted">Shows at the foot of the About page</span>
      </div>
      <p className="text-[12px] ek-text-muted -mt-2">
        Track which version / release the live platform is on. Displayed publicly on About so the team always
        knows what's shipped.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Version" value={sv.version} onChange={(v) => setSv({ ...sv, version: v })} testid="site-version-input" placeholder="1.0.0" />
        <Field label="Release" value={sv.release} onChange={(v) => setSv({ ...sv, release: v })} testid="site-release-input" placeholder="R1" />
        <Field label="Released on" value={sv.released_on} onChange={(v) => setSv({ ...sv, released_on: v })} testid="site-released-on-input" placeholder="July 2026" />
      </div>
      <Field label="Release notes (short)" rows={2} value={sv.notes} onChange={(v) => setSv({ ...sv, notes: v })} testid="site-version-notes-input" placeholder="What changed in this release" />

      <div className={`border px-4 py-3 text-[12px] ${mismatch ? "border-amber-300 bg-amber-50" : "ek-line ek-surface-2"}`} data-testid="site-build-tag-panel">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold tracking-[0.18em] uppercase ek-text-muted">Deployed build</span>
          <code className="px-1.5 py-0.5 ek-surface border ek-line ek-text-soft" data-testid="deployed-build-tag">{buildTag}</code>
        </div>
        {mismatch ? (
          <p className="mt-2 text-amber-800" data-testid="site-version-mismatch">
            The declared version <strong>{sv.version}</strong> doesn't appear in the deployed build tag — update the
            version above (or the deploy's <code>REACT_APP_RELEASE</code>) so they match.
          </p>
        ) : (
          <p className="mt-2 ek-text-muted" data-testid="site-version-match">
            {buildTag === "dev"
              ? "No release tag on this build (preview/dev). Set REACT_APP_RELEASE at deploy to auto-verify."
              : "Declared version matches the deployed build tag ✓"}
          </p>
        )}
      </div>

      <button onClick={save} disabled={saving} className="ek-btn ek-btn-primary text-[13px]" data-testid="site-version-save-btn">
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save site version
      </button>
    </div>
  );
}

function ContentReadiness() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/content/readiness"); setData(data); }
    catch { /* non-blocking */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { items, summary } = data;
  const allOk = summary.warn === 0;

  return (
    <div className="ek-surface border ek-line p-6" data-testid="content-readiness">
      <div className="flex items-center justify-between mb-1">
        <h3 className="ek-heading text-lg">Content readiness</h3>
        <span
          className={`text-[11px] font-semibold px-2 py-0.5 ${allOk ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}
          data-testid="content-readiness-summary"
        >
          {allOk ? "All set ✓" : `${summary.warn} to review`}
        </span>
      </div>
      <p className="text-[12px] ek-text-muted mb-4">Owner-supplied content gaps to close before go-live.</p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} className="flex items-start gap-3" data-testid={`readiness-item-${it.id}`}>
            {it.status === "ok"
              ? <CheckCircle2 size={16} className="mt-0.5 flex-none text-emerald-600" />
              : <AlertTriangle size={16} className="mt-0.5 flex-none text-amber-500" />}
            <div className="text-[13px]">
              <div className="font-medium ek-text">{it.label}</div>
              <div className={it.status === "ok" ? "ek-text-muted" : "text-amber-800"}>{it.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SiteContentPanel() {
  return (
    <div className="space-y-6" data-testid="site-content-panel">
      <ContentReadiness />
      <SiteVersionEditor />
      <AboutEditor />
      <TestimonialsEditor />
      <LegalSignoffEditor />
    </div>
  );
}
